import React, { useState, useEffect, useContext } from 'react';
import { 
    FaSave, FaBuilding, FaUserTie, FaPhone, 
    FaMapMarkerAlt, FaInfoCircle, FaUniversity, FaCity 
} from 'react-icons/fa';
import api from '../../api/axios';
import { toast } from 'react-toastify';
import { AppContext } from '../../context/AppContext';

const ManagerSettings = () => {
    const { user } = useContext(AppContext);
    
    const [formData, setFormData] = useState({
        name: '',
        manager_name: '',
        manager_phone: '',
        city: '',
        address: ''
    });
    const [teamId, setTeamId] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    // Load Nama Manajer dari Akun Login
    useEffect(() => {
        if (user) {
            setFormData(prev => ({ ...prev, manager_name: user.name }));
        }
    }, [user]);

    useEffect(() => {
        const fetchMyTeam = async () => {
            try {
                const response = await api.get('/teams/me');
                const data = response.data;
                
                if (data) {
                    setFormData({
                        name: data.name || '',
                        manager_name: data.manager_name || user?.name || '',
                        manager_phone: data.manager_phone || '',
                        city: data.city || '',
                        address: data.address || ''
                    });
                    setTeamId(data.id);
                    setIsEditMode(true);
                }
            } catch (error) {
                console.error("Gagal load data tim:", error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchMyTeam();
    }, [user]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        try {
            if (isEditMode) {
                await api.put(`/teams/${teamId}`, formData);
                toast.success("Profil tim berhasil diperbarui!");
            } else {
                await api.post('/teams', formData);
                toast.success("Tim berhasil dibuat!");
                setIsEditMode(true);
            }
        } catch (error) {
            toast.error(error.response?.data?.msg || "Gagal menyimpan data.");
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '70vh' }}>
                <div className="spinner-border text-primary" role="status"></div>
            </div>
        );
    }

    return (
        <div className="container-fluid p-0 animate-fade-in">
            {/* HEADER */}
            <div className="mb-4">
                <h4 className="fw-bold mb-1 text-dark d-flex align-items-center gap-2">
                    <FaBuilding className="text-primary"/> Profil Kontingen
                </h4>
                <p className="text-muted small mb-0">Atur identitas tim dan informasi penanggung jawab kontingen.</p>
            </div>

            <div className="row g-4">
                <div className="col-lg-8">
                    <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
                        <div className="card-header bg-white p-4 border-bottom">
                            <h6 className="fw-bold text-dark m-0">Informasi Dasar Tim</h6>
                        </div>
                        <div className="card-body p-4 p-md-5">
                            <form onSubmit={handleSubmit}>
                                {/* SEKSI 1: IDENTITAS TIM */}
                                <div className="mb-5">
                                    <h6 className="text-primary fw-bold small text-uppercase mb-4" style={{ letterSpacing: '1px' }}>
                                        <FaUniversity className="me-2"/> Identitas Organisasi
                                    </h6>
                                    <div className="row g-4">
                                        <div className="col-12">
                                            <label className="form-label small fw-bold text-muted">Nama Tim / Klub / Sekolah</label>
                                            <div className="input-group shadow-sm rounded-3 overflow-hidden">
                                                <span className="input-group-text bg-light border-0"><FaBuilding className="text-muted" /></span>
                                                <input 
                                                    type="text" 
                                                    className="form-control border-0 py-2 bg-light" 
                                                    placeholder="Contoh: Dojang Garuda Indonesia"
                                                    required
                                                    value={formData.name}
                                                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label small fw-bold text-muted">Asal Kota/Kabupaten</label>
                                            <div className="input-group shadow-sm rounded-3 overflow-hidden">
                                                <span className="input-group-text bg-light border-0"><FaCity className="text-muted" /></span>
                                                <input 
                                                    type="text" 
                                                    className="form-control border-0 py-2 bg-light" 
                                                    placeholder="Masukkan kota asal..."
                                                    required
                                                    value={formData.city}
                                                    onChange={(e) => setFormData({...formData, city: e.target.value})}
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-12">
                                            <label className="form-label small fw-bold text-muted">Alamat Lengkap</label>
                                            <div className="input-group shadow-sm rounded-3 overflow-hidden">
                                                <span className="input-group-text bg-light border-0"><FaMapMarkerAlt className="text-muted" /></span>
                                                <textarea 
                                                    className="form-control border-0 py-2 bg-light" 
                                                    rows="2"
                                                    placeholder="Alamat lengkap operasional tim..."
                                                    required
                                                    value={formData.address}
                                                    onChange={(e) => setFormData({...formData, address: e.target.value})}
                                                ></textarea>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <hr className="my-5 opacity-25" />

                                {/* SEKSI 2: MANAJER */}
                                <div className="mb-5">
                                    <h6 className="text-primary fw-bold small text-uppercase mb-4" style={{ letterSpacing: '1px' }}>
                                        <FaUserTie className="me-2"/> Data Penanggung Jawab
                                    </h6>
                                    <div className="row g-4">
                                        <div className="col-md-6">
                                            <label className="form-label small fw-bold text-muted">Nama Manajer (Otomatis)</label>
                                            <div className="input-group shadow-sm rounded-3 overflow-hidden">
                                                <span className="input-group-text bg-secondary bg-opacity-10 border-0"><FaUserTie className="text-muted" /></span>
                                                <input 
                                                    type="text" 
                                                    className="form-control border-0 py-2 bg-secondary bg-opacity-10" 
                                                    value={formData.manager_name}
                                                    disabled
                                                />
                                            </div>
                                            <small className="text-muted x-small">*Diambil dari nama profil akun Anda.</small>
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label small fw-bold text-muted">Nomor WhatsApp Aktif</label>
                                            <div className="input-group shadow-sm rounded-3 overflow-hidden">
                                                <span className="input-group-text bg-light border-0"><FaPhone className="text-muted" /></span>
                                                <input 
                                                    type="text" 
                                                    className="form-control border-0 py-2 bg-light" 
                                                    placeholder="Contoh: 08123456789"
                                                    required
                                                    value={formData.manager_phone}
                                                    onChange={(e) => setFormData({...formData, manager_phone: e.target.value})}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="d-flex justify-content-end">
                                    <button 
                                        type="submit" 
                                        className="btn btn-primary px-5 py-3 rounded-pill fw-bold shadow-sm d-flex align-items-center gap-2 hover-scale"
                                        disabled={isSaving}
                                    >
                                        {isSaving ? (
                                            <><div className="spinner-border spinner-border-sm"></div> Menyimpan...</>
                                        ) : (
                                            <><FaSave /> {isEditMode ? "Simpan Perubahan" : "Konfirmasi & Buat Tim"}</>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>

                {/* SIDE INFO */}
                <div className="col-lg-4">
                    <div className="card border-0 shadow-sm rounded-4 bg-primary bg-opacity-10 h-100 overflow-hidden position-relative">
                        <div className="position-absolute end-0 top-0 p-4 opacity-25">
                            <FaInfoCircle size={100} />
                        </div>
                        <div className="card-body p-4 position-relative z-1">
                            <h6 className="fw-bold text-dark mb-4 d-flex align-items-center gap-2">
                                <FaInfoCircle className="text-primary"/> Petunjuk Pengisian
                            </h6>
                            <ul className="text-dark small ps-3 mb-0 d-flex flex-column gap-3" style={{ lineHeight: '1.6' }}>
                                <li>
                                    <span className="fw-bold">Nama Tim:</span> Gunakan nama resmi karena akan tercetak secara otomatis pada sertifikat atlet dan dokumen bagan.
                                </li>
                                <li>
                                    <span className="fw-bold">Nomor WhatsApp:</span> Pastikan nomor aktif untuk mempermudah koordinasi penimbangan dan pemanggilan partai oleh panitia.
                                </li>
                                <li>
                                    <span className="fw-bold">Kota:</span> Digunakan untuk klasemen medali berdasarkan wilayah jika diperlukan.
                                </li>
                            </ul>
                            
                            <div className="mt-5 p-3 bg-white rounded-4 shadow-sm border-0 small text-muted">
                                <FaBuilding className="me-2 text-primary"/> Status Tim: 
                                <span className={isEditMode ? "text-success fw-bold ms-1" : "text-warning fw-bold ms-1"}>
                                    {isEditMode ? "Aktif & Terdaftar" : "Belum Dibuat"}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <style>{`
                .animate-fade-in { animation: fadeIn 0.4s ease-out forwards; }
                .hover-scale { transition: transform 0.2s; }
                .hover-scale:hover { transform: scale(1.02); }
                .x-small { font-size: 0.75rem; }
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
            `}</style>
        </div>
    );
};

export default ManagerSettings;